import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft,
  Car,
  User,
  DollarSign,
  Camera,
  Upload,
  X,
  Plus,
  MessageCircle,
  AlertTriangle,
  Check,
  Clock,
  Loader2,
  Trash2,
  Share2,
  Copy,
  ExternalLink
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = [
  { value: 'reception', label: 'Recepción', icon: Clock, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'in_progress', label: 'En Progreso', icon: Clock, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'completed', label: 'Completado', icon: Check, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'delivered', label: 'Entregado', icon: Check, color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

const severityLabels = {
  low: { label: 'Bajo', color: 'bg-slate-50 border-slate-200' },
  medium: { label: 'Medio', color: 'bg-amber-50 border-amber-200' },
  high: { label: 'Alto', color: 'bg-red-50 border-red-200' }
};

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [service, setService] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadType, setUploadType] = useState(null); // 'before', 'during', 'after'
  const [uploading, setUploading] = useState(false);
  
  const [editData, setEditData] = useState({
    income: 0,
    costs: 0,
    notes: '',
    status: 'reception'
  });
  
  const [newFinding, setNewFinding] = useState({
    description: '',
    severity: 'medium',
    estimated_cost: ''
  });
  const [showFindingForm, setShowFindingForm] = useState(false);

  useEffect(() => {
    fetchService();
  }, [serviceId]);

  const fetchService = async () => {
    try {
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Servicio no encontrado');
      }
      
      const data = await response.json();
      setService(data);
      setEditData({
        income: data.income || 0,
        costs: data.costs || 0,
        notes: data.notes || '',
        status: data.status
      });
      
      // Fetch vehicle and client info
      const [vehicleRes, clientRes] = await Promise.all([
        fetch(`${API_URL}/api/vehicles/${data.vehicle_id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/clients/${data.client_id}`, { credentials: 'include' })
      ]);
      
      if (vehicleRes.ok) setVehicle(await vehicleRes.json());
      if (clientRes.ok) setClient(await clientRes.json());
    } catch (error) {
      toast.error(error.message);
      navigate('/services');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          income: parseFloat(editData.income) || 0,
          costs: parseFloat(editData.costs) || 0,
          notes: editData.notes,
          status: editData.status
        })
      });
      
      if (!response.ok) throw new Error('Error al guardar');
      
      const updated = await response.json();
      setService(updated);
      toast.success('Cambios guardados');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadType) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!uploadRes.ok) throw new Error('Error al subir imagen');
      
      const { url } = await uploadRes.json();
      
      // Update service with new image
      const imagesKey = `images_${uploadType}`;
      const currentImages = service[imagesKey] || [];
      
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          [imagesKey]: [...currentImages, url]
        })
      });
      
      if (!response.ok) throw new Error('Error al actualizar servicio');
      
      const updated = await response.json();
      setService(updated);
      toast.success('Imagen agregada');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
      setUploadType(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async (type, index) => {
    try {
      const imagesKey = `images_${type}`;
      const currentImages = [...(service[imagesKey] || [])];
      currentImages.splice(index, 1);
      
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [imagesKey]: currentImages })
      });
      
      if (!response.ok) throw new Error('Error al eliminar imagen');
      
      const updated = await response.json();
      setService(updated);
      toast.success('Imagen eliminada');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddFinding = async () => {
    if (!newFinding.description.trim()) {
      toast.error('Ingresa una descripción');
      return;
    }
    
    try {
      const findings = [...(service.findings || []), {
        description: newFinding.description,
        severity: newFinding.severity,
        estimated_cost: parseFloat(newFinding.estimated_cost) || null
      }];
      
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ findings })
      });
      
      if (!response.ok) throw new Error('Error al agregar hallazgo');
      
      const updated = await response.json();
      setService(updated);
      setNewFinding({ description: '', severity: 'medium', estimated_cost: '' });
      setShowFindingForm(false);
      toast.success('Hallazgo agregado');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveFinding = async (index) => {
    try {
      const findings = [...(service.findings || [])];
      findings.splice(index, 1);
      
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ findings })
      });
      
      if (!response.ok) throw new Error('Error al eliminar hallazgo');
      
      const updated = await response.json();
      setService(updated);
      toast.success('Hallazgo eliminado');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const sendFindingsWhatsApp = () => {
    if (!client?.whatsapp || !service?.findings?.length) {
      toast.error('No hay hallazgos o número de WhatsApp');
      return;
    }
    
    let message = `🔧 *Hallazgos Adicionales*\n\n`;
    message += `Vehículo: ${vehicle?.brand} ${vehicle?.model} (${vehicle?.plates})\n`;
    message += `Servicio: ${service.description}\n\n`;
    
    service.findings.forEach((f, i) => {
      const severityIcon = f.severity === 'high' ? '🔴' : f.severity === 'medium' ? '🟡' : '⚪';
      message += `${i + 1}. ${severityIcon} ${f.description}`;
      if (f.estimated_cost) message += ` - $${f.estimated_cost.toLocaleString('es-MX')}`;
      message += '\n';
    });
    
    const cleanPhone = client.whatsapp.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getTrackingUrl = () => {
    return `${window.location.origin}/consulta?code=${service?.tracking_code}`;
  };

  const copyTrackingCode = () => {
    navigator.clipboard.writeText(service?.tracking_code || '');
    toast.success('Código copiado');
  };

  const shareTrackingWhatsApp = () => {
    if (!client?.whatsapp) {
      toast.error('El cliente no tiene WhatsApp registrado');
      return;
    }
    
    let message = `🔧 *Estado de tu Servicio*\n\n`;
    message += `Hola ${client.name}, puedes consultar el estado de tu servicio en cualquier momento:\n\n`;
    message += `📋 Código: *${service.tracking_code}*\n`;
    message += `🔗 Link: ${getTrackingUrl()}\n\n`;
    message += `Vehículo: ${vehicle?.brand} ${vehicle?.model} (${vehicle?.plates})\n`;
    message += `Servicio: ${service.description}`;
    
    const cleanPhone = client.whatsapp.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="animate-fade-in">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!service) return null;

  const profit = (editData.income || 0) - (editData.costs || 0);

  return (
    <div className="animate-fade-in" data-testid="service-detail-page">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate('/services')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{service.description}</h1>
          <p className="text-slate-500">Servicio #{serviceId.slice(-8)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Workflow */}
          <div className="card-base p-4">
            <h3 className="font-semibold text-slate-900 mb-4">Estado del Servicio</h3>
            <div className="status-workflow">
              {statusOptions.map((status, index) => {
                const StatusIcon = status.icon;
                const isActive = editData.status === status.value;
                const statusIndex = statusOptions.findIndex(s => s.value === editData.status);
                const isPast = index < statusIndex;
                
                return (
                  <button
                    key={status.value}
                    onClick={() => setEditData({ ...editData, status: status.value })}
                    className={`status-step ${
                      isActive ? 'status-step-active' : 
                      isPast ? 'status-step-completed' : 'status-step-pending'
                    }`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    {status.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Vehicle & Client Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {vehicle && (
              <Link to={`/vehicles/${vehicle.vehicle_id}`} className="card-base card-hover p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Car className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{vehicle.brand} {vehicle.model}</p>
                  <p className="text-sm text-slate-500">{vehicle.plates} • {vehicle.year}</p>
                </div>
              </Link>
            )}
            {client && (
              <Link to={`/clients/${client.client_id}`} className="card-base card-hover p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{client.name}</p>
                  <p className="text-sm text-slate-500">{client.whatsapp || client.email || 'Sin contacto'}</p>
                </div>
              </Link>
            )}
          </div>

          {/* Images Sections */}
          {['before', 'during', 'after'].map((type) => {
            const images = service[`images_${type}`] || [];
            const labels = { before: 'Antes', during: 'Durante', after: 'Después' };
            
            return (
              <div key={type} className="card-base p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">
                    <Camera className="w-4 h-4 inline mr-2" />
                    Fotos {labels[type]}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUploadType(type);
                      fileInputRef.current?.click();
                    }}
                    disabled={uploading}
                  >
                    {uploading && uploadType === type ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Agregar
                      </>
                    )}
                  </Button>
                </div>
                
                {images.length > 0 ? (
                  <div className="image-gallery">
                    {images.map((url, i) => (
                      <div key={i} className="image-gallery-item">
                        <img src={`${API_URL}${url}`} alt={`${labels[type]} ${i + 1}`} />
                        <div className="image-gallery-item-overlay">
                          <button
                            onClick={() => handleRemoveImage(type, i)}
                            className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No hay fotos {labels[type].toLowerCase()}
                  </p>
                )}
              </div>
            );
          })}

          {/* Findings */}
          <div className="card-base p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">
                <AlertTriangle className="w-4 h-4 inline mr-2 text-amber-500" />
                Hallazgos Adicionales
              </h3>
              <div className="flex gap-2">
                {(service.findings?.length > 0 && client?.whatsapp) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendFindingsWhatsApp}
                    className="text-[#25D366] border-[#25D366] hover:bg-[#25D366]/10"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    Enviar WhatsApp
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFindingForm(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>
            </div>
            
            {service.findings?.length > 0 ? (
              <div className="space-y-3">
                {service.findings.map((finding, i) => (
                  <div 
                    key={i} 
                    className={`finding-card ${severityLabels[finding.severity]?.color || ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-xs font-medium uppercase text-slate-500">
                          Prioridad {severityLabels[finding.severity]?.label}
                        </span>
                        <p className="text-slate-800">{finding.description}</p>
                        {finding.estimated_cost && (
                          <p className="text-sm text-slate-600 mt-1">
                            Costo estimado: ${finding.estimated_cost.toLocaleString('es-MX')}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveFinding(i)}
                        className="p-1 hover:bg-red-100 rounded text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                No se han registrado hallazgos adicionales
              </p>
            )}
            
            {/* Finding Form */}
            {showFindingForm && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg space-y-3">
                <Textarea
                  placeholder="Describe el hallazgo..."
                  value={newFinding.description}
                  onChange={(e) => setNewFinding({ ...newFinding, description: e.target.value })}
                  rows={2}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    value={newFinding.severity}
                    onValueChange={(value) => setNewFinding({ ...newFinding, severity: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Prioridad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Bajo</SelectItem>
                      <SelectItem value="medium">Medio</SelectItem>
                      <SelectItem value="high">Alto</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Costo estimado"
                    value={newFinding.estimated_cost}
                    onChange={(e) => setNewFinding({ ...newFinding, estimated_cost: e.target.value })}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowFindingForm(false)}>
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={handleAddFinding} className="bg-[#1E3A5F]">
                    Agregar Hallazgo
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tracking Code Card */}
          <div className="card-base p-4 bg-gradient-to-br from-[#1E3A5F] to-[#0F172A] text-white">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Código de Seguimiento
            </h3>
            
            <div className="bg-white/10 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xl tracking-wider">{service.tracking_code || 'N/A'}</span>
                <button 
                  onClick={copyTrackingCode}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Copiar código"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <p className="text-white/70 text-xs mb-3">
              El cliente puede consultar el estado de su servicio en cualquier momento
            </p>
            
            <div className="space-y-2">
              <Button
                onClick={shareTrackingWhatsApp}
                className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white"
                size="sm"
                data-testid="share-tracking-whatsapp"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Enviar por WhatsApp
              </Button>
              
              <a
                href={getTrackingUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-white/10 hover:bg-white/20 rounded-md text-sm transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Ver página de cliente
              </a>
            </div>
          </div>

          {/* Finance Card */}
          <div className="card-base p-4">
            <h3 className="font-semibold text-slate-900 mb-4">
              <DollarSign className="w-4 h-4 inline mr-2" />
              Finanzas del Servicio
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="income">Ingreso ($)</Label>
                <Input
                  id="income"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editData.income}
                  onChange={(e) => setEditData({ ...editData, income: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="costs">Costos ($)</Label>
                <Input
                  id="costs"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editData.costs}
                  onChange={(e) => setEditData({ ...editData, costs: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              
              <div className="p-3 rounded-lg bg-slate-50">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-500">Ingreso</span>
                  <span className="text-emerald-600 font-medium">${(editData.income || 0).toLocaleString('es-MX')}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-500">Costos</span>
                  <span className="text-red-600 font-medium">-${(editData.costs || 0).toLocaleString('es-MX')}</span>
                </div>
                <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between">
                  <span className="font-medium text-slate-700">Utilidad</span>
                  <span className={`font-bold ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    ${profit.toLocaleString('es-MX')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card-base p-4">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              rows={4}
              className="mt-2"
              placeholder="Notas adicionales..."
            />
          </div>

          {/* Save Button */}
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-[#1E3A5F] hover:bg-[#162e4d]"
            data-testid="save-service-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Guardar Cambios
          </Button>
        </div>
      </div>
    </div>
  );
}
